import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Brand } from "@/pages/Login";

const EARN = [
  ["$24.50", "per property, every month", "You keep half of each property's $49 monthly subscription for as long as it stays subscribed."],
  ["Your fee", "for every install", "You set and bill your own installation fee directly to the property. We don't take a cut."],
];
const STEPS = [
  ["Apply", "Sign up as an MSP. We review and approve each partner."],
  ["Brand your page", "Add your name, logo and colour. Properties see only your brand."],
  ["Share your link", "Properties sign up and pay for their module and subscription on your page."],
  ["Install the DS-1", "Place it as the gateway on the property network and name each area (front desk, guest Wi-Fi…)."],
  ["Mark it complete", "Confirm the system is operating from your dashboard. Lease balances are charged then."],
];
const PRICES = [
  ["Buy the module", "$400 today", "$49/month"],
  ["Lease purchase", "$200 today, $200 once installed", "$49/month"],
];
const SCALE = [10, 25, 50, 100];

const Section = ({ id, kicker, title, children }) => (
  <section id={id} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 border-t border-[#222D3D]">
    <p className="font-mono text-xs tracking-[0.3em] uppercase text-emerald-400">{kicker}</p>
    <h2 className="mt-3 text-2xl sm:text-3xl font-semibold text-slate-50 max-w-2xl">{title}</h2>
    <div className="mt-10">{children}</div>
  </section>
);

const Apply = ({ id }) => (
  <Button asChild className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid={id}>
    <Link to="/msp/signup">Apply as an MSP <ArrowRight className="h-4 w-4 ml-2" /></Link>
  </Button>
);

export default function Partners() {
  return (
    <div className="min-h-screen bg-[#07090E] text-slate-200">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#07090E]/85 border-b border-[#222D3D]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="partners-brand-link"><Brand /></Link>
          <Link to="/" className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100 transition-colors" data-testid="partners-home-link"><ArrowLeft className="h-4 w-4" />Home</Link>
        </div>
      </header>

      <section className="grid-bg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 fade-up">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-emerald-400">MSP partner program</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] text-slate-50 max-w-3xl" data-testid="partners-title">
            Sell AI agent visibility under your own name.
          </h1>
          <p className="mt-6 text-base text-slate-400 max-w-xl">Install the DS-1 at your clients' properties, keep half of every subscription and charge your own install fee.</p>
          <div className="mt-10"><Apply id="partners-hero-apply-btn" /></div>
        </div>
      </section>

      <Section id="earn" kicker="How you earn" title="Recurring revenue plus your own install fee">
        <div className="grid md:grid-cols-2 gap-4">
          {EARN.map(([big, small, text], i) => (
            <div key={big} className="rounded-lg border border-[#222D3D] bg-[#0F141C] p-6" data-testid={`partners-earn-${i}`}>
              <p className="font-display text-4xl text-emerald-400">{big}</p>
              <p className="mt-1 text-sm text-slate-300">{small}</p>
              <p className="mt-4 text-sm text-slate-400">{text}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 rounded-lg border border-[#222D3D] overflow-hidden" data-testid="partners-scale-table">
          <div className="grid grid-cols-2 sm:grid-cols-4">
            {SCALE.map((n) => (
              <div key={n} className="p-5 border-[#222D3D] [&:nth-child(odd)]:border-r [&:nth-child(-n+2)]:border-b sm:border-b-0 sm:border-r sm:last:border-r-0">
                <p className="text-xs text-slate-500">{n} properties</p>
                <p className="mt-1 font-display text-xl text-slate-100">${(n * 24.5).toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(n * 24.5) ? 0 : 2 })}<span className="text-xs text-slate-500">/mo</span></p>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-500">Your 50% share of the $49 monthly subscription, before your install fees.</p>
      </Section>

      <Section id="installs" kicker="How installs work" title="From sign-up to a live ledger in five steps">
        <ol className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {STEPS.map(([t, d], i) => (
            <li key={t} className="rounded-lg border border-[#222D3D] bg-[#0F141C] p-5" data-testid={`partners-step-${i + 1}`}>
              <p className="font-mono text-xs text-emerald-400">0{i + 1}</p>
              <p className="mt-3 font-semibold text-slate-100">{t}</p>
              <p className="mt-2 text-sm text-slate-400">{d}</p>
            </li>
          ))}
        </ol>
      </Section>

      <Section id="pricing" kicker="What properties pay" title="Simple pricing for your clients">
        <div className="rounded-lg border border-[#222D3D] bg-[#0F141C] divide-y divide-[#222D3D]" data-testid="partners-pricing">
          {PRICES.map(([plan, module, monthly]) => (
            <div key={plan} className="grid sm:grid-cols-3 gap-2 p-5">
              <p className="font-semibold text-slate-100">{plan}</p>
              <p className="text-sm text-slate-300">DS-1 module: {module}</p>
              <p className="text-sm text-slate-300">Subscription: {monthly}, starts at sign-up</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-500">Properties pay securely on your branded page. Your install fee is billed by you, separately.</p>
      </Section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-[#222D3D] flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-semibold text-slate-50">Ready to offer it to your clients?</h2>
          <p className="mt-2 text-sm text-slate-400">Apply in two minutes. We'll review and approve you shortly.</p>
        </div>
        <Apply id="partners-footer-apply-btn" />
      </section>
    </div>
  );
}
